import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Skeleton, Result } from "antd";
import Swal from "sweetalert2";
import TitleBar from "../../common/title-bar/title-bar.js";
import Button from "../../common/button/button.js";
import TabFade from "../../common/tab-fade";
import { useAuth } from "../AuthProvider.js";
import UserDetailsTab from "./UserDetailsTab";
import UserPermissionsTab from "./UserPermissionsTab";
import { canAdminPerformUserAction } from "./userActionAccess.js";
import {
  getUser,
  getUserActivity,
  updateUser,
  inviteUser,
  resendInvite,
  revokeUser,
  deleteUser,
  getUserStatus,
  getUserStatusType,
  getUserRoleLabel,
  USER_TYPES,
  USER_STATUS,
} from "../../Service/userService";
import "./UserDetails.css";

/**
 * Unified user management page: `/User/:id`.
 *
 * Renders any user type (Viewer / Account / Manufacturer / Distributor). The
 * Role-Specific fields and the header action buttons are shown conditionally
 * per the Figma annotations (see UserDetailsTab + the button block below).
 */
const UserDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const [user, setUser] = useState(null);
  const [values, setValues] = useState({});
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [pageState, setPageState] = useState("loading");
  const [selectedTab, setSelectedTab] = useState("Details");
  const [isEditable, setIsEditable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const seedValues = (u) => ({
    FirstName: u.FirstName,
    LastName: u.LastName,
    Phone: u.Phone,
    Prefix: u.Prefix,
    ContactEmail: u.ContactEmail,
    AccountContactType: u.IsProvider ? "Provider" : u.AccountContactType,
    ProviderNPI: u.ProviderNPI,
    RegionName: u.RegionName,
    AccountName: u.AccountName,
    ManufacturerName: u.ManufacturerName,
    DistributorName: u.DistributorName,
    IsManager: u.IsManager,
  });

  const fetchUser = useCallback(async () => {
    setPageState("loading");
    try {
      const data = await getUser(id);
      if (!data || !data.UserType) {
        setPageState("notFound");
        return;
      }
      setUser(data);
      setValues(seedValues(data));
      setPageState("loaded");
    } catch (err) {
      console.error("Failed to fetch user", err);
      setPageState(
        err?.response?.status === 404 || err?.response?.status === 400
          ? "notFound"
          : "error"
      );
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    let cancelled = false;
    setActivityLoading(true);
    getUserActivity(id)
      .then((data) => !cancelled && setActivity(data))
      .finally(() => !cancelled && setActivityLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const setValue = (field, val) =>
    setValues((prev) => ({ ...prev, [field]: val }));

  const status = useMemo(() => getUserStatus(user), [user]);
  const canEditUser = canAdminPerformUserAction(authUser, "user.edit");
  const canEditAccountUserType = canAdminPerformUserAction(
    authUser,
    "user.edit",
  );
  const canInviteUser = canAdminPerformUserAction(authUser, "user.invite");
  const canRevokeUser = canAdminPerformUserAction(authUser, "user.revoke");
  const canDeleteUser = canAdminPerformUserAction(authUser, "user.delete");
  const canViewUserPermissions = canAdminPerformUserAction(
    authUser,
    "user.permissions.view",
  ) && Boolean(authUser?.IsSuperAdmin);

  // -------------------------
  // Handlers
  // -------------------------
  const handleEdit = () => {
    if (!canEditUser) return;
    setIsEditable(true);
  };

  const handleSave = async () => {
    if (!canEditUser) return;
    const isAccountUser = user.UserType === USER_TYPES.ACCOUNT;
    const convertingToProvider =
      isAccountUser &&
      !user.IsProvider &&
      values.AccountContactType === "Provider";

    if (
      isAccountUser &&
      values.AccountContactType === "Provider" &&
      !/^\d{10}$/.test(values.ProviderNPI || "")
    ) {
      await Swal.fire(
        "Provider NPI required",
        "Provider NPI must be exactly 10 digits.",
        "error",
      );
      return;
    }

    if (convertingToProvider) {
      const result = await Swal.fire({
        title: "Convert this user to a provider?",
        text: "They keep their current Tissue Vault access. Provider conversion cannot be reversed from this page.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Convert to Provider",
      });
      if (!result.isConfirmed) return;
    }

    const payload = {
      UserID: user.UserID,
      UserType: user.UserType,
      FirstName: values.FirstName,
      LastName: values.LastName,
      Phone: values.Phone,
      RegionName: values.RegionName,
      AccountName: values.AccountName,
      ManufacturerName: values.ManufacturerName,
      DistributorName: values.DistributorName,
      IsManager: values.IsManager,
    };

    if (
      isAccountUser &&
      canEditAccountUserType &&
      values.AccountContactType
    ) {
      payload.AccountContactType = values.AccountContactType;
      if (values.AccountContactType === "Provider") {
        payload.Prefix = values.Prefix;
        payload.ContactEmail = values.ContactEmail;
        payload.ProviderNPI = values.ProviderNPI;
      }
    }

    setIsSaving(true);
    try {
      await updateUser(payload);
      Swal.fire("Saved", "User details have been updated.", "success");
      setIsEditable(false);
      fetchUser();
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Failed to update user details.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const confirmAndRun = async (title, text, action, successMsg, onSuccess) => {
    const result = await Swal.fire({
      title,
      text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2663EB",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    });
    if (!result.isConfirmed) return;
    try {
      await action(user);
      Swal.fire("Done", successMsg, "success").then(
        () => (onSuccess ? onSuccess() : fetchUser())
      );
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Something went wrong.",
        "error"
      );
    }
  };

  const handleSendInvite = () => {
    if (!canInviteUser) return;
    return confirmAndRun(
      "Send invite?",
      `Send an invite to ${user.Email || "this user"}?`,
      inviteUser,
      "Invite sent."
    );
  };

  const handleResendInvite = () => {
    if (!canInviteUser) return;
    return confirmAndRun(
      "Resend invite?",
      `Resend the invite to ${user.Email || "this user"}?`,
      resendInvite,
      "Invite resent."
    );
  };

  const handleRevoke = () => {
    if (!canRevokeUser) return;
    return confirmAndRun(
      "Revoke access?",
      "This user will lose access to Tissue Vault.",
      revokeUser,
      "Access revoked."
    );
  };

  const handleDelete = () => {
    if (!canDeleteUser) return;
    const fullName = `${user.FirstName || ""} ${user.LastName || ""}`.trim();
    return confirmAndRun(
      "Delete user?",
      `${fullName || "This user"} will be removed from Tissue Vault and will no longer appear anywhere in the portal. This cannot be undone.`,
      deleteUser,
      "User deleted.",
      () => navigate("/Users")
    );
  };

  // -------------------------
  // Render states
  // -------------------------
  if (pageState === "loading") {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }
  if (pageState === "notFound") {
    return (
      <Result
        status="404"
        title="User Does Not Exist"
        subTitle={`No accessible user exists for ID ${id || "(missing)"}.`}
        extra={<Button onClick={() => navigate("/Users")}>Back to Users</Button>}
      />
    );
  }
  if (pageState === "error") {
    return (
      <Result
        status="error"
        title="Unable to Load User"
        subTitle="An unexpected error occurred while loading this user."
        extra={<Button onClick={fetchUser}>Retry</Button>}
      />
    );
  }

  const fullName = `${user.FirstName || ""} ${user.LastName || ""}`.trim() || "User";
  const displayStatus = status;

  // Action buttons — visibility follows the Figma annotations.
  const actionButtons = (
      <div className="user-details-actions">
        {canEditUser && isEditable ? (
          <Button
            variant="table"
            subVariant="success"
            style={{ minWidth: "100px" }}
            disabled={isSaving}
            onClick={handleSave}
          >
            Save
          </Button>
        ) : canEditUser ? (
          <Button
            variant="table"
            subVariant="warning"
            style={{ minWidth: "100px" }}
            onClick={handleEdit}
          >
            Edit
          </Button>
        ) : null}
        {status === USER_STATUS.NOT_INVITED && canInviteUser && (
          <Button
            variant="table"
            subVariant="primary"
            style={{ minWidth: "100px" }}
            onClick={handleSendInvite}
          >
            Send Invite
          </Button>
        )}
        {status === USER_STATUS.INVITE_SENT && canInviteUser && (
          <Button
            variant="table"
            subVariant="primary"
            style={{ minWidth: "100px" }}
            onClick={handleResendInvite}
          >
            Resend Invite
          </Button>
        )}
        {status === USER_STATUS.ACTIVE && canRevokeUser && (
          <Button
            variant="table"
            subVariant="failure"
            style={{ minWidth: "100px" }}
            onClick={handleRevoke}
          >
            Revoke Access
          </Button>
        )}
        {status === USER_STATUS.REVOKED && canInviteUser && (
          <Button
            variant="table"
            subVariant="primary"
            style={{ minWidth: "100px" }}
            onClick={handleSendInvite}
          >
            Send Invite
          </Button>
        )}
        {/* Delete is offered for every user type and status, providers
            included — for them it removes the contact rather than access. */}
        {canDeleteUser && (
          <Button
            variant="table"
            subVariant="failure"
            style={{ minWidth: "100px" }}
            onClick={handleDelete}
          >
            Delete
          </Button>
        )}
      </div>
    );

  return (
    <div>
      <TitleBar
        entityName="Users"
        entityNameLink="/Users"
        label={fullName}
        invoiceNumber={getUserRoleLabel(user)}
        statusIndicatorStatus={displayStatus}
        statusIndicatorStatusType={getUserStatusType(displayStatus)}
        filterBarComponents={
          <>
            <Button
              variant="timeframe"
              isActive={selectedTab === "Details"}
              onClick={() => setSelectedTab("Details")}
            >
              Details
            </Button>
            {canViewUserPermissions && (
              <Button
                variant="timeframe"
                isActive={selectedTab === "Permissions"}
                onClick={() => setSelectedTab("Permissions")}
              >
                Permissions
              </Button>
            )}
          </>
        }
        rightFilterBarComponents={
          selectedTab === "Details" ? actionButtons : null
        }
      />

      <TabFade activeKey={selectedTab} style={{ marginTop: "20px" }}>
        {selectedTab === "Details" && (
          <UserDetailsTab
            user={user}
            values={values}
            setValue={setValue}
            isEditable={isEditable}
            canEditAccountUserType={canEditAccountUserType}
            activity={activity}
            activityLoading={activityLoading}
          />
        )}
        {selectedTab === "Permissions" && canViewUserPermissions && (
          <UserPermissionsTab userId={user.UserID} userType={user.UserType} />
        )}
      </TabFade>
    </div>
  );
};

export default UserDetailsPage;
